import { useDeckContext } from '@context/DeckContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { LayoutTemplateSummary } from '@services/api/deckManagement/graphql/schema/API';
import { useGetColumnTemplateCatalog } from '@services/api/deckManagement/hooks';
import { useMemo } from 'react';

/**
 * Table Configuration step.
 *
 * New pages are configured by selecting an existing reusable PageLayout family
 * from the deck's template catalog. The selected family is later assigned to
 * every created grain Page (see PageSetupContext.assignLayoutFamily). Custom
 * per-column authoring is intentionally out of scope here.
 */
const TableConfiguration = () => {
  const { currDeckId, selectedLayoutFamily, setSelectedLayoutFamily } = usePageSetupContext();
  const { currentDeck } = useDeckContext();

  const {
    data: catalog,
    isLoading,
    isError,
  } = useGetColumnTemplateCatalog({
    deckId: currDeckId || undefined,
    // Family-wide query that still resolves the cycle-specific template where one
    // exists, mirroring how getPageLayout resolves a page at render time. Shared
    // (DECK_CYCLE_ANY) templates are still returned for a concrete cycle.
    deckCycle: currentDeck?.deck_cycle || undefined,
  });

  /**
   * De-duplicate catalog summaries into selectable layout families (family + display name),
   * sorted alphabetically. Grain coverage is intentionally NOT enforced at selection time:
   * templates are not necessarily authored 1:1 per page grain (e.g. Summary), so every catalog
   * family is selectable; getPageLayout resolves each grain at render time.
   */
  const families = useMemo(() => {
    const items: LayoutTemplateSummary[] = (catalog?.items || []).filter((item): item is LayoutTemplateSummary =>
      Boolean(item?.layout_family),
    );

    const byFamily = new Map<string, string>(); // family → display name
    items.forEach((item) => {
      const family = item.layout_family as string;
      if (!byFamily.has(family)) byFamily.set(family, item.name || family);
    });

    const derived = Array.from(byFamily.entries())
      .map(([family, name]) => ({ family, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Ensure the page's already-assigned family is always present as an option (edit flow),
    // even if it isn't returned by the catalog for this deck/cycle.
    if (selectedLayoutFamily && !derived.some((entry) => entry.family === selectedLayoutFamily)) {
      derived.push({ family: selectedLayoutFamily, name: selectedLayoutFamily });
    }

    return derived;
  }, [catalog, selectedLayoutFamily]);

  return (
    <Box className='table-configuration-step'>
      <Typography variant='h6'>Column Template Settings</Typography>

      {isError && <Alert severity='error'>Failed to load the template catalog for this deck.</Alert>}

      <FormControl className='layout-family-selector' fullWidth margin='normal' variant='outlined' size='small'>
        <InputLabel>Layout Template</InputLabel>
        <Select
          value={selectedLayoutFamily}
          onChange={(event) => setSelectedLayoutFamily(event.target.value)}
          label='Layout Template'
          disabled={isLoading || isError}
        >
          {families.map(({ family, name }) => (
            <MenuItem key={family} value={family}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TableConfiguration;
